import React, { useEffect, useState } from "react";
import axios from "axios";
import { AppstoreAddOutlined } from "@ant-design/icons";
import {
  Space,
  Card,
  Layout,
  Form,
  Input,
  Checkbox,
  Button,
  Col,
  Row,
  Table,
  Upload,
  message,
  Modal,
} from "antd";
import SideBar from "./_sideBar";
import CopyRight from "./CopyRight";

const { Content } = Layout;

function BankManagement() {
  const [form] = Form.useForm();
  const [bankList, setBankList] = useState([]);
  const [loading, setLoading] = useState(false);
  const loginUserToken = JSON.parse(localStorage.getItem("loginUserToken"));
  const [isEditMode, setIsEditMode] = useState(false);
  const [editBankId, setEditBankId] = useState(null);

  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BACKEND_URL}/v1/bank`,
        {
          headers: {
            Authorization: `Bearer ${loginUserToken}`,
          },
        },
      );
      setBankList(response.data);
    } catch (error) {
      console.error("Error fetching banks:", error);
      message.error("Failed to fetch bank list");
    }
  };

  const saveBank = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("bankType", values.bankType);
      formData.append("paymentOption", values.paymentMethod);
      formData.append("bankCharges", values.bankCharges);
      formData.append("status", values.status);

      let url = "";
      let method = "";
      if (isEditMode) {
        // UPDATE
        if (
          values.logo &&
          values.logo.length > 0 &&
          values.logo[0].originFileObj
        ) {
          formData.append("logoFile", values.logo[0].originFileObj);
        }
        formData.append("id", editBankId);
        url = `${process.env.REACT_APP_API_BACKEND_URL}/v1/bank/update`;
        method = "put";
      } else {
        if (
          values.logo &&
          values.logo.length > 0 &&
          values.logo[0].originFileObj
        ) {
          formData.append("logoFile", values.logo[0].originFileObj);
        }
        // CREATE
        url = `${process.env.REACT_APP_API_BACKEND_URL}/v1/bank/create`;
        method = "post";
      }
      await axios({
        method: method,
        url: url,
        data: formData,
        headers: {
          Authorization: `Bearer ${loginUserToken}`,
          "Content-Type": "multipart/form-data",
        },
      });
      message.success(
        isEditMode
          ? "Bank updated successfully!"
          : "Bank created successfully!",
      );

      // Reset everything
      fetchBanks();
      form.resetFields();
      setIsEditMode(false);
      setEditBankId(null);
    } catch (error) {
      console.error("Error saving bank:", error);
      message.error("Failed to save bank. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BACKEND_URL}/v1/bank/update-status`,
        {
          id,
          status: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${loginUserToken}`,
          },
        },
      );
      message.success("Status updated successfully!");
      fetchBanks();
    } catch (error) {
      console.error(
        " Error updating status:",
        error.response?.data || error.message,
      );
      message.error("Failed to update status");
    }
  };

  const startEdit = async (record) => {
    form.setFieldsValue({
      id: record.id,
      name: record.name,
      bankType: record.bankType,
      paymentMethod: record.paymentMethod,
      bankCharges: record.bankCharges,
      status: record.status,
      logo: record.logoPath
        ? [
            {
              uid: "-1",
              name: record.logoPath || "logo.png",
              status: "done",
              url: `${process.env.REACT_APP_API_BACKEND_URL}/images/${record.logoPath}`,
            },
          ]
        : [],
    });

    setEditBankId(record.id);
    setIsEditMode(true);
  };

  const columns = [
    {
      title: "Bank Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Bank Type",
      dataIndex: "bankType",
      key: "bankType",
    },
    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
    },
    {
      title: "Bank Charges",
      dataIndex: "bankCharges",
      key: "bankCharges",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (status ? "Active" : "Inactive"),
    },
    {
      title: "Logo",
      dataIndex: "logoPath",
      key: "logoPath",
      render: (logo) =>
        logo ? (
          <img
            src={`${process.env.REACT_APP_API_IMAGE_URL}/images/${logo}`}
            alt="Bank Logo"
            style={{ width: 50, height: 50 }}
          />
        ) : (
          "No Logo"
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Button type="link" onClick={() => startEdit(record)}>
            Edit
          </Button>
          <Button
            type="link"
            onClick={() => handleStatusChange(record.id, !record.status)}
          >
            Set {record.status ? "Inactive" : "Active"}
          </Button>
        </>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <SideBar fontSize="14px" />
      <Layout className="site-layout">
        <Content>
          <div className="site-layout-background" style={{ minHeight: 360 }}>
            <Space
              direction="vertical"
              style={{ padding: "10px", width: "100%" }}
            >
              <Card
                title={
                  <span>
                    <AppstoreAddOutlined style={{ marginRight: 8 }} />
                    Bank Management
                  </span>
                }
                headStyle={{ backgroundColor: "#cccccc" }}
              >
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={saveBank}
                  style={{ maxWidth: "600px", margin: "0 auto" }}
                >
                  <Form.Item
                    label="Bank Name"
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter the bank name",
                      },
                    ]}
                  >
                    <Input placeholder="Enter bank name" />
                  </Form.Item>

                  <Form.Item
                    label="Bank Type"
                    name="bankType"
                    rules={[
                      {
                        required: true,
                        message: "Please enter the bank type",
                      },
                    ]}
                  >
                    <Input placeholder="Enter bank type" />
                  </Form.Item>

                  <Form.Item
                    label="Payment method"
                    name="paymentMethod"
                    rules={[
                      {
                        required: true,
                        message: "Please enter the payment method",
                      },
                    ]}
                  >
                    <Input placeholder="Enter payment method" />
                  </Form.Item>

                  <Form.Item
                    label="Bank Charges"
                    name="bankCharges"
                    rules={[
                      {
                        required: true,
                        message: "Please enter the bank charges",
                      },
                    ]}
                  >
                    <Input placeholder="Enter bank charges" type="number" />
                  </Form.Item>

                  <Form.Item
                    label="Bank Logo"
                    name="logo"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => {
                      console.log("Upload event:", e);
                      return e.fileList;
                    }}
                  >
                    <Upload
                      listType="picture"
                      maxCount={1}
                      beforeUpload={() => false}
                    >
                      <Button>Click to Upload</Button>
                    </Upload>
                  </Form.Item>

                  <Form.Item name="status" valuePropName="checked">
                    <Checkbox>Bank Status</Checkbox>
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      {isEditMode ? "Update Bank" : "Create Bank"}
                    </Button>

                    {isEditMode && (
                      <Button
                        style={{ marginLeft: 10 }}
                        onClick={() => {
                          form.resetFields();
                          setIsEditMode(false);
                          setEditBankId(null);
                        }}
                      >
                        Cancel Edit
                      </Button>
                    )}
                  </Form.Item>
                </Form>
              </Card>

              <Table
                dataSource={bankList}
                columns={columns}
                rowKey="id"
                pagination={{ pageSize: 5 }}
                bordered
              />
            </Space>
          </div>
          <CopyRight />
        </Content>
      </Layout>
    </Layout>
  );
}

export default BankManagement;
